/**
 * Tag Data Type Enum
 * Defines the available data types for tags and parameters
 */
export enum TagDataType {
  Int = 'Int',
  Integer = 'integer',
  String = 'string',
  Bool = 'Bool',
  Boolean = 'boolean',
  Invisible = 'invisible'
}

/**
 * Helper class for TagDataType operations
 */
export class TagDataTypeHelper {
  /**
   * Get all data type options for dropdown.
   * Note: The enum can contain multiple representations of the same type (Int/Integer, Bool/Boolean).
   * We de-duplicate them here so the UI doesn't show duplicates.
   *
   * Requirement: Show "Integer" instead of "Int", and "Boolean" instead of "Bool" in the UI.
   */
  static getAllOptions(): { value: string; label: string }[] {
    // Put the preferred labels first.
    // The value is normalized (Int/Bool) so the backend receives a consistent format.
    const raw = [
      { value: TagDataType.Integer, label: 'Integer' },
      { value: TagDataType.Int, label: 'Int' },
      { value: TagDataType.String, label: 'String' },
      { value: TagDataType.Boolean, label: 'Boolean' },
      { value: TagDataType.Bool, label: 'Bool' },
      { value: TagDataType.Invisible, label: 'Invisible' }
    ];

    const seen = new Set<string>();
    const result: { value: string; label: string }[] = [];

    for (const opt of raw) {
      const normalized = TagDataTypeHelper.normalize(opt.value);
      const key = (normalized || '').toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);

      // Return the normalized value (consistent backend format),
      // while keeping the preferred UI label (Integer/Boolean).
      result.push({ value: normalized, label: opt.label });
    }

    return result;
  }

  /**
   * Check if data type is integer type
   */
  static isInteger(dataType: string): boolean {
    return dataType === TagDataType.Int || dataType === TagDataType.Integer;
  }

  /**
   * Check if data type is string type
   */
  static isString(dataType: string): boolean {
    return dataType === TagDataType.String;
  }

  /**
   * Check if data type is boolean type
   */
  static isBoolean(dataType: string): boolean {
    return dataType === TagDataType.Bool || dataType === TagDataType.Boolean;
  }

  /**
   * Check if data type is invisible
   */
  static isInvisible(dataType: string): boolean {
    return dataType === TagDataType.Invisible;
  }

  /**
   * Normalize data type to standard format
   */
  static normalize(dataType: string): string {
    switch (dataType?.toLowerCase()) {
      case 'int':
      case 'integer':
        return TagDataType.Int;
      case 'string':
        return TagDataType.String;
      case 'bool':
      case 'boolean':
        return TagDataType.Bool;
      case 'invisible':
        return TagDataType.Invisible;
      default:
        return dataType;
    }
  }
}
